import './styles.scss'

import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import Skeleton from 'react-loading-skeleton'

import FormatPrice from '@/components/FormatPrice'
import { InfoIcon } from '@/components/Icons'
import { useAppSelector } from '@/store/hook'
import { selectCheckoutSetting } from '@/store/selectors/cartSelector'
import { useDeviceDetect } from '@/hooks'
import usePermission from '@/hooks/usePermission'
import { MODULE_CART_CHECKOUT_RIGHTS } from '@/libs/constants/modulerights'

import CartItemName from '../../CartItemName'
import CartItemQuantityWrapper from '../../CartItemTable/CartItemQuantityWrapper'
import RemoveCartItem from '../../RemoveCartItem'

interface CartItemInvalidProps {
  cartItem?: {
    imgUrl?: string | Record<string, unknown>
    materialNumber?: string | Record<string, unknown> | number
    quantity?: number
    price?: number
    total?: number
    error?: string
    emptyRow?: boolean
    deliveryData?: Record<string, unknown>[]
    currencyUnit?: string
  }
  isLoading?: boolean
}

const CartItemInvalid = ({
  cartItem,
  isLoading = false,
}: CartItemInvalidProps) => {
  const { t } = useTranslation()
  const { isExtremeSmallMobile } = useDeviceDetect()

  const {
    price,
    total,
    error,
    emptyRow,
    materialNumber,
    imgUrl,
    currencyUnit,
  } = cartItem

  const { hasPermission: hasViewIndividualPricePermission } = usePermission(
    MODULE_CART_CHECKOUT_RIGHTS.VIEW_INDIVIDUAL_PRICE,
  )

  const { positionLvlConditions: shouldShowRecyclingFee } = useAppSelector(
    selectCheckoutSetting,
  )

  const renderTopBlock = () => {
    const classes = clsx(
      'cart-item-mobile__block',
      'cart-item-mobile__right',
      'cart-item-mobile__right--full-width',
      emptyRow && 'cart-item-mobile__right--remove-margin',
    )

    const renderName = () => {
      return isLoading ? <Skeleton /> : (
        <CartItemName
          isMobile
          data={cartItem}
          shouldShowRecyclingFee={shouldShowRecyclingFee}
        />
      )
    }

    const renderMaterialNumber = () => {
      if (isLoading) {
        return <Skeleton />
      }

      return error
        ? (
          <>
            <InfoIcon style={{ verticalAlign: 'middle' }} /> ${materialNumber}
          </>
        )
        : materialNumber
    }

    return (
      <div className={classes}>
        <div className="cart-item-mobile__left__top">{imgUrl}</div>
        <div className="cart-item-mobile__right__top">
          {emptyRow ? null : <span className="cart-item-mobile__title">{renderName()}</span>}
          <span className="cart-item-mobile__item-number">
            {emptyRow ? null : `${t('cart.table.itemNo')}: `}
            {renderMaterialNumber()}
          </span>
        </div>
        <RemoveCartItem
          haveText={false}
          materialNumber={materialNumber}
          className={clsx(
            emptyRow && 'cart-item-mobile__remove-button',
            'cart-item-mobile__remove-button--empty-row',
          )}
        />
      </div>
    )
  }

  const renderListPriceBlock = () => {
    return (
      <div className="cart-item-mobile__price-block cart-item-mobile__list-price">
        <span className="cart-item-mobile__list-price__text">
          {t('cart.table.individualPrice')}
        </span>
        <span className="cart-item-mobile__list-price__value">
          {isLoading ? <Skeleton /> : <FormatPrice currencyUnit={currencyUnit} price={price} />}
        </span>
      </div>
    )
  }

  const renderTotalPriceBlock = () => {
    return (
      <div className="cart-item-mobile__price-block cart-item-mobile__total-price">
        <span className="cart-item-mobile__total-price__text">
          {t('total')}
        </span>
        <span className="cart-item-mobile__total-price__value">
          {isLoading ? <Skeleton /> : <FormatPrice currencyUnit={currencyUnit} price={total} />}
        </span>
      </div>
    )
  }

  const renderBottomLeftBlock = () => {
    const classes = clsx(
      'cart-item-mobile__block',
      isExtremeSmallMobile
        ? 'cart-item-mobile__quantity-section--empty-row cart-item-mobile__bottom'
        : 'cart-item-mobile__left',
    )

    const mainContent = <CartItemQuantityWrapper isParentDisabled={!!error || emptyRow} />
    return (
      <div className={classes}>
        <div className="cart-item-mobile__left__bottom">
          {isLoading ? <Skeleton /> : mainContent}
        </div>
      </div>
    )
  }

  return (
    <div className="cart-item-mobile__wrapper">
      {renderTopBlock()}
      {renderBottomLeftBlock()}
      <div
        className={clsx(
          'cart-item-mobile__block',
          isExtremeSmallMobile
            ? 'cart-item-mobile__bottommm'
            : 'cart-item-mobile__right',
        )}
      >
        {hasViewIndividualPricePermission && (
          <div className="cart-item-mobile__right__bottom">
            {renderListPriceBlock()}
            {renderTotalPriceBlock()}
          </div>
        )}
      </div>
    </div>
  )
}

export default CartItemInvalid
